import { useEffect, useState } from "react";
import { Box, Heading, Stack, Text } from "@chakra-ui/react";
import { useAuth } from "react-oidc-context";
import { CreatorsApi, type CreatorStats } from "../api/creators";
import { WorksApi, type WorkStats } from "../api/works";
import StatsPanel from "../components/StatsPanel";
import WeeklyScorecard from "../components/WeeklyScorecard";
import { deriveCallerEmail } from "../utils/callerEmail";
import logger from "../analytics/logger";

export default function Welcome() {
  const auth = useAuth();
  const email = deriveCallerEmail(auth.user?.profile as Record<string, unknown> | undefined);

  const [creatorStats, setCreatorStats] = useState<CreatorStats | null>(null);
  const [workStats, setWorkStats] = useState<WorkStats | null>(null);

  useEffect(() => {
    CreatorsApi.stats()
      .then(setCreatorStats)
      .catch((e) => logger.error("welcome_creator_stats_failed", e));
    WorksApi.stats()
      .then(setWorkStats)
      .catch((e) => logger.error("welcome_work_stats_failed", e));
  }, []);

  return (
    <Box p={8}>
      <Heading size="lg">Welcome</Heading>
      <Text mt={1} color="gray.600">
        Logged in as {email ?? "(no email claim)"}
      </Text>

      <Stack spacing={8} mt={8}>
        <WeeklyScorecard />

        <Box>
          <Heading size="md" mb={3}>
            Creators
          </Heading>
          {creatorStats ? (
            <StatsPanel
              sections={[
                {
                  title: "By status",
                  entries: [
                    {
                      label: "Total",
                      value: Object.values(creatorStats.by_status).reduce(
                        (sum, v) => sum + v,
                        0,
                      ),
                    },
                    ...Object.entries(creatorStats.by_status).map(([k, v]) => ({
                      label: k,
                      value: v,
                    })),
                  ],
                },
                {
                  title: "Recently added",
                  entries: [
                    { label: "Past 24h", value: creatorStats.added.day },
                    { label: "Past 7d", value: creatorStats.added.week },
                    { label: "Past 30d", value: creatorStats.added.month },
                  ],
                },
              ]}
            />
          ) : (
            <Text color="gray.500">Loading…</Text>
          )}
        </Box>

        <Box>
          <Heading size="md" mb={3}>
            Works
          </Heading>
          {workStats ? (
            <StatsPanel
              sections={[
                {
                  title: "By status (latest cert)",
                  entries: Object.entries(workStats.by_status).map(([k, v]) => ({
                    label: k,
                    value: v,
                  })),
                },
                {
                  title: "Approved by type",
                  entries: Object.entries(workStats.by_type_approved).map(([k, v]) => ({
                    label: k,
                    value: v,
                  })),
                },
                {
                  title: "Recently added",
                  entries: [
                    { label: "Past 24h", value: workStats.added.day },
                    { label: "Past 7d", value: workStats.added.week },
                    { label: "Past 30d", value: workStats.added.month },
                  ],
                },
              ]}
            />
          ) : (
            <Text color="gray.500">Loading…</Text>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
